import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormFillFields, { FormLocaleSwitch } from './FormFillFields'
import FormBrandingHeader from './FormBrandingHeader'
import { DEFAULT_LOCALE, normalizeLocales, resolveFormDisplayTitle } from './formBuilderModel'

export default function FormPreviewModal({ formId, formName, open, onClose }) {
  const [definition, setDefinition] = useState(null)
  const [settings, setSettings] = useState(null)
  const [name, setName] = useState(formName || '')
  const [facing, setFacing] = useState('internal')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [locale, setLocale] = useState(DEFAULT_LOCALE)

  useEffect(() => {
    if (!open || !formId) {
      setDefinition(null)
      setSettings(null)
      setError('')
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError('')
    authFetch(getLocalApiUrl(`/api/forms/${formId}/preview`))
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return
        if (!j.success) throw new Error(j.error || 'Failed to load preview')
        setDefinition(j.data?.version?.definition || null)
        setSettings(j.data?.version?.settings || null)
        setName(j.data?.form?.name || formName || 'Form')
        setFacing(j.data?.form?.facing || 'internal')
        const locales = normalizeLocales(j.data?.version?.settings)
        setLocale(locales[0] || DEFAULT_LOCALE)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load preview')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open, formId, formName])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-slate-200">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Form preview</div>
            <h2 className="text-lg font-bold text-[#073B4D] m-0 mt-0.5 truncate">{name}</h2>
            <p className="text-xs text-slate-500 m-0 mt-1">Published version — preview only, answers are not saved.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error ? <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div> : null}
          {loading ? <div className="text-sm text-slate-500">Loading preview…</div> : null}
          {!loading && definition && settings ? (
            <>
              {facing === 'patron' ? (
                <FormLocaleSwitch settings={settings} locale={locale} onChange={setLocale} className="mb-4" />
              ) : null}
              <FormBrandingHeader formName={resolveFormDisplayTitle(settings, name, facing === 'patron' ? locale : DEFAULT_LOCALE)} />
              <FormFillFields
                definition={definition}
                settings={settings}
                locale={facing === 'patron' ? locale : DEFAULT_LOCALE}
                answers={{}}
                readOnly
                onChange={() => {}}
              />
            </>
          ) : null}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end shrink-0 bg-slate-50/80 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  )
}
